import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import Label from '@/app/common/components/Label'
import DistributionRulesCard from '../shared/DistributionRulesCard'
import AutoDistributionCard from '../shared/AutoDistributionCard'
import { DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConfirmChangeRuleDialog } from '../shared/ChangeRuleDialog'
import { useState } from 'react'
import { PartialPurseData } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/types.ts'
import {
  ModifierCode,
  ProgramPurseResource,
} from '@/queries/program_purses/schemas.ts'
import { useForm } from 'react-hook-form'
import { useUpdateFixedDistribution } from '@/queries/purse_configs/useUpdateFixedDistribution.ts'
import { useQueryClient } from '@tanstack/react-query'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import ControlledCurrencyInput from '../shared/ControlledCurrencyInput.tsx'
import { SubmitButton } from '@/app/common/components/SubmitButton.tsx'
import { useUpdateProgramPurse } from '@/queries/purse_configs/useUpdateProgramPurse.ts'
import SpecialNeedsAwardCard from '../shared/SpecialNeedsAwardCard.tsx'
import { ExtraModifierData } from '../modifiers/types.ts'
import { ExtraModifierCards } from '../shared/ExtraModifierCard.tsx'

type UIVariant = 'form_view' | 'all_settings_view'

type Props = {
  variant: UIVariant
  partialPurseData: PartialPurseData
  fixedAmountRule: ProgramPurseResource['distributions_rules']['fixed_distribution']
  onBack?: () => void
  onSuccessfulSave: () => void
  onChangeRule?: () => void
  onNavigateToSpecialNeedsModifier: () => void
  extraModifierData?: ExtraModifierData
  onConfigureModifier?: (code: ModifierCode) => void
}

type FormValues = {
  base_amount: number
}

export default function FixedAmountRuleDetails({
  variant,
  partialPurseData,
  fixedAmountRule,
  // navigation buttons
  onBack, // configure → back to chooser
  onSuccessfulSave, // configure/view → save action
  onChangeRule, // view → back to chooser
  onNavigateToSpecialNeedsModifier,
  extraModifierData,
  onConfigureModifier,
}: Props) {
  const isView = variant === 'all_settings_view'
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { toast } = useToast()

  const { control, handleSubmit, reset } = useForm<FormValues>({
    values: {
      base_amount: fixedAmountRule.base_amount,
    },
  })

  const queryClient = useQueryClient()
  const updateFixedMutation = useUpdateFixedDistribution()
  const updatePurseMutation = useUpdateProgramPurse()
  const isSubmitting =
    updateFixedMutation.status === 'pending' ||
    updatePurseMutation.status === 'pending'
  const onSubmit = async (data: FormValues) => {
    updatePurseMutation.mutate(
      {
        params: {
          purse_id: partialPurseData.id,
          program_id: partialPurseData.programId,
        },
        payload: { awarding_method_code: 'fixed_amount' },
      },
      {
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to update fixed distribution',
          })
        },
      }
    )

    updateFixedMutation.mutate(
      {
        params: {
          programId: partialPurseData.programId,
          purseId: partialPurseData.id,
        },
        payload: { base_amount: Number(data.base_amount ?? 0) },
      },
      {
        onSuccess: () => {
          reset(data)
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(partialPurseData.programId),
          })
          onSuccessfulSave()
          toast({
            title: 'Success',
            description: 'Fixed distribution updated successfully',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to update fixed distribution',
          })
        },
      }
    )
  }

  return (
    <div className="w-full px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {isView && (
          <>
            <AutoDistributionCard partialPurseData={partialPurseData} />
            <DistributionRulesCard />
          </>
        )}

        {isView && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <DollarSign className="h-6 w-6" aria-hidden />
                  <div className="mt-2 text-sm font-medium text-gray-900">
                    Fixed Dollar Amount
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    All eligible students receive the same scholarship amount.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                >
                  Change Rule
                </Button>

                <ConfirmChangeRuleDialog
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  onConfirm={onChangeRule ?? (() => {})}
                />
              </div>
            </CardHeader>
          </Card>
        )}

        <form id="fixed-distribution-rule" onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardHeader>
              {!isView && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                    Fixed Dollar Amount
                  </h3>
                  <p className="mt-0.5 mb-8 text-sm text-gray-600">
                    Enter the amount each eligible student will receive.
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Base Amount
                </h3>
                <p className="mt-0.5 text-sm text-gray-600">
                  The amount every grade will receive
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                <Label htmlFor="fixed_amount" weight="medium">
                  Amount
                </Label>
                <div className="relative max-w-md">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                  >
                    $
                  </span>
                  <ControlledCurrencyInput
                    id="fixed_amount"
                    name={'base_amount'}
                    control={control}
                    min={0}
                    decimalsLimit={2}
                    className="pl-6"
                    placeholder="0"
                  />
                </div>
              </div>

              <div
                className={cn(
                  'mt-6 flex gap-3',
                  isView ? 'justify-start' : 'justify-end'
                )}
              >
                {isView ? (
                  <SubmitButton
                    status={updateFixedMutation.status}
                    form="fixed-distribution-rule"
                    variant="primary"
                    size="sm"
                    className="w-auto"
                    buttonTxt="Save Changes"
                  />
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onBack}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <SubmitButton
                      status={updateFixedMutation.status}
                      form="fixed-distribution-rule"
                      variant="primary"
                      size="sm"
                      className="w-auto"
                      buttonTxt="Continue"
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </form>
        {isView && (
          <>
            <SpecialNeedsAwardCard
              onConfigure={onNavigateToSpecialNeedsModifier}
            />
            {extraModifierData && onConfigureModifier && (
              <ExtraModifierCards
                modifiers={extraModifierData}
                onConfigureModifier={onConfigureModifier}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
